import React, { useState, useEffect, useRef } from 'react';
import {
  BrowserRouter as Router,
  Link,
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import styled from 'styled-components';
import * as Sentry from '@sentry/react';
import PrivateRoute from './PrivateRoute';
import AdminRoute from './AdminRoute';
import UnifiedFeed from './pages/UnifiedFeed';
import Prompts from './pages/Prompts';
import StreamingPreferences from './pages/StreamingPreferences';
import Admin from './pages/Admin';
import { AuthContext } from './context/auth';
import { HeavyText, LightItalicText, LoadingText } from './components/Style';
import CreatePost from './pages/CreatePost';
import CreateList from './pages/CreateList';
import EditList from './pages/EditList';
import ChooseUsername from './pages/ChooseUsername';
import EditProfile from './pages/EditProfile';
import Profile from './pages/Profile';
import Landing from './pages/Landing';
import Invites from './pages/Invites';
import Notifications from './pages/Notifications';
import Bookmarks from './pages/Bookmarks';
import UserSearch from './pages/UserSearch';
import PostDetail from './pages/PostDetail';
import GoogleCallback from './pages/GoogleCallback';
import TagFeed from './pages/TagFeed';
import PromptFeed from './pages/PromptFeed';
import SongPage from './pages/SongPage';
import ArtistPage from './pages/ArtistPage';
import MusicSearch from './pages/MusicSearch';
import { setApiAuthToken, API_BASE, COOKIE_SESSION } from './api';
import api from './api';
import { ReactComponent as Person } from './img/person-circle.svg';
import { ReactComponent as Bell } from './img/bell.svg';
import { PlaybackProvider } from './context/playback';
import { PlayCountsProvider } from './context/playCounts';
import MiniPlayer from './components/MiniPlayer';
import ScrollToTopButton from './components/ScrollToTopButton';
import SignupPromptModal from './components/SignupPromptModal';
import { VinylDisc, VINYL_THEMES } from './components/UserBadge';
import { identifyUser, resetUser, trackPageView } from './analytics';
var Login = React.lazy(function () {
  return import('./pages/Login');
});
var Signup = React.lazy(function () {
  return import('./pages/Signup');
});
var Help = React.lazy(function () {
  return import('./pages/Help');
});
var Guide = React.lazy(function () {
  return import('./pages/Guide');
});
var Legal = React.lazy(function () {
  return import('./pages/Legal');
});
var Privacy = React.lazy(function () {
  return import('./pages/Privacy');
});
var Welcome = React.lazy(function () {
  return import('./pages/Welcome');
});
var Terms = React.lazy(function () {
  return import('./pages/Terms');
});
var Disclaimer = React.lazy(function () {
  return import('./pages/Disclaimer');
});
var ForgotPassword = React.lazy(function () {
  return import('./pages/ForgotPassword');
});
var ResetPassword = React.lazy(function () {
  return import('./pages/ResetPassword');
});
var VerifyEmail = React.lazy(function () {
  return import('./pages/VerifyEmail');
});

// Flip to true to enable artist/song/album pages
var ENABLE_MUSIC_PAGES = true;

const ServerDownOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.25rem;
  padding: 2rem;
  text-align: center;
`;

const ServerDownTitle = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
`;

const ServerDownMessage = styled.div`
  font-size: 0.85rem;
  color: #666;
  max-width: 280px;
  line-height: 1.4;
`;

const ServerDownRefresh = styled.button`
  background: black;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.6rem 1.5rem;
  font-size: 0.85rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  margin-top: 0.5rem;

  &:hover {
    opacity: 0.85;
  }
`;

const ContentWrapper = styled.div`
  /* Room for the player bar, which sits above the iPhone's home indicator. */
  padding-bottom: calc(60px + env(safe-area-inset-bottom, 0px));
  padding-left: 1rem;
  padding-right: 1rem;

  @media (max-width: 360px) {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
`;

var SiteFooter = styled.footer`
  text-align: center;
  padding: 3rem 1rem 1.5rem;
  font-size: 0.65rem;
  color: #bbb;
  line-height: 1.8;
`;

var FooterLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 1em;
  margin-bottom: 0.4em;
`;

var FooterLink = styled(Link)`
  color: #bbb;
  text-decoration: none;

  &:hover {
    color: #999;
  }
`;

const NavBar = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5em 1.5em;
  border-bottom: 1px solid #eee;
  background: white;
`;

const NavLeft = styled.div`
  width: 100px;
  display: flex;
  align-items: center;
  gap: 0.5em;
`;

const NavCenter = styled.div`
  font-size: x-large;
  white-space: nowrap;
`;

const NavRight = styled.div`
  width: 100px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5em;
`;

const PlusButtonEl = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: white;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  color: black;
  font-size: 1.4rem;
  font-weight: 300;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
`;

const SearchLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  text-decoration: none;
  color: black;

  @media (max-width: 600px) {
    display: none;
  }
`;

const NavProfilePic = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
`;

const NavLoginLink = styled(Link)`
  font-size: 0.75rem;
  text-decoration: none;
  color: black;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const HamburgerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  position: relative;
`;

var CreateMenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
`;

var CreateMenuPopup = styled.div`
  position: fixed;
  background: white;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  padding: 4px 0;
  z-index: 1000;
  min-width: 140px;
`;

var CreateMenuItem = styled(Link)`
  display: block;
  width: 100%;
  background: none;
  border: none;
  padding: 8px 12px;
  font-size: 0.8rem;
  font-weight: 700;
  color: black;
  text-align: left;
  cursor: pointer;
  text-decoration: none;
  box-sizing: border-box;

  &:hover {
    background: #f0f0f0;
  }
`;

// The + used to link straight to /post. With lists there are several things
// you can create, so it opens a small menu: a post, or a list already set to
// the style picked (Create List reads ?style=).
function CreateMenu() {
  var _open = useState(false);
  var open = _open[0];
  var setOpen = _open[1];
  var btnRef = useRef(null);

  return (
    <>
      <PlusButtonEl
        ref={btnRef}
        onClick={function () {
          setOpen(!open);
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Create"
      >
        +
      </PlusButtonEl>
      {open && (
        <>
          <CreateMenuOverlay
            onClick={function () {
              setOpen(false);
            }}
          />
          <CreateMenuPopup
            style={{
              top: btnRef.current
                ? btnRef.current.getBoundingClientRect().bottom + 4 + 'px'
                : '48px',
              left: '8px',
            }}
          >
            <CreateMenuItem
              to="/post"
              onClick={function () {
                setOpen(false);
              }}
            >
              Post
            </CreateMenuItem>
            <CreateMenuItem
              to="/create-list?style=grid"
              onClick={function () {
                setOpen(false);
              }}
            >
              3x3
            </CreateMenuItem>
            <CreateMenuItem
              to="/create-list?style=playlist"
              onClick={function () {
                setOpen(false);
              }}
            >
              Playlist
            </CreateMenuItem>
            <CreateMenuItem
              to="/create-list?style=ranking"
              onClick={function () {
                setOpen(false);
              }}
            >
              Ranked List
            </CreateMenuItem>
          </CreateMenuPopup>
        </>
      )}
    </>
  );
}

function HamburgerIcon() {
  return (
    <svg width="18" height="14" viewBox="0 0 18 14" fill="none" style={{ display: 'block' }}>
      <rect y="0" width="18" height="2" rx="1" fill="black" />
      <rect y="6" width="18" height="2" rx="1" fill="black" />
      <rect y="12" width="18" height="2" rx="1" fill="black" />
    </svg>
  );
}

const UnreadDotNav = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: tomato;
`;

const MenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
`;

const MenuDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.25rem;
  background: white;
  border: 1px solid #ddd;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 12px;
  min-width: 180px;
  z-index: 1000;
  overflow: hidden;
`;

const MenuItem = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  text-decoration: none;
  color: black;
  font-size: 0.85rem;
  font-weight: 600;

  &:hover {
    background: #f5f5f5;
  }

  & + & {
    border-top: 1px solid #eee;
  }
`;

const MenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  text-decoration: none;
  color: black;
  font-size: 0.85rem;
  font-weight: 600;
  background: none;
  border: none;
  width: 100%;
  cursor: pointer;
  border-top: 1px solid #eee;

  &:hover {
    background: #f5f5f5;
  }
`;

const MenuBadge = styled.span`
  background: tomato;
  color: white;
  font-size: 0.6rem;
  font-weight: 700;
  min-width: 16px;
  height: 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
`;

// Intercept clicks on plain <a> tags (from dangerouslySetInnerHTML, e.g. formatText pills)
// and route them through React Router to avoid full page reloads that kill playback.
function InternalLinkInterceptor() {
  var navigate = useNavigate();
  useEffect(
    function () {
      function handleClick(e) {
        var target = e.target;
        while (target && target.tagName !== 'A') {
          target = target.parentElement;
        }
        if (!target || !target.href) return;
        // Only intercept links marked with data-mention-link (from formatText)
        if (!target.hasAttribute('data-mention-link')) return;
        try {
          var url = new URL(target.href);
          if (url.origin !== window.location.origin) return;
          if (e.metaKey || e.ctrlKey || e.shiftKey) return;
          e.preventDefault();
          e.stopPropagation();
          navigate(url.pathname + url.search + url.hash);
        } catch (err) {}
      }
      document.addEventListener('click', handleClick, true);
      return function () {
        document.removeEventListener('click', handleClick, true);
      };
    },
    [navigate]
  );
  return null;
}

function PageViewTracker() {
  var location = useLocation();
  useEffect(
    function () {
      // Store previous path before updating to current
      var current = sessionStorage.getItem('hr_current_path');
      if (current) {
        sessionStorage.setItem('hr_previous_path', current);
      }
      sessionStorage.setItem('hr_current_path', location.pathname);
      // Capture ?ref= param for referral attribution on any page
      var refParam = new URLSearchParams(window.location.search).get('ref');
      if (refParam) {
        localStorage.setItem('signup_referrer', refParam);
      }
      trackPageView();
    },
    [location.pathname]
  );
  return null;
}

function App(props) {
  const [auth, setAuth] = useState(function () {
    var stored = localStorage.getItem('authData');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        return null;
      }
    }
    return null;
  });

  // True only while we are asking the server whether an hr_session cookie can
  // restore a session. iOS wipes localStorage after 7 days without a visit, so
  // "no stored auth" does not mean "logged out" until we have checked. Gating
  // the first render on that check avoids flashing the signed-out UI at
  // someone who is still signed in.
  // hr_session_present is a server-set, credential-free companion to the
  // HttpOnly session cookie. It survives the storage purge that removes
  // localStorage, so it is the only reliable signal that a restore is worth
  // waiting for. Anonymous visitors never have it and are not delayed.
  var restoringState = useState(function () {
    return (
      !localStorage.getItem('authData') && document.cookie.indexOf('hr_session_present=1') !== -1
    );
  });
  var restoringSession = restoringState[0];
  var setRestoringSession = restoringState[1];

  var unreadState = useState(0);
  var unreadCount = unreadState[0];
  var setUnreadCount = unreadState[1];

  var menuState = useState(false);
  var menuOpen = menuState[0];
  var setMenuOpen = menuState[1];

  var connectionState = useState(false);
  var showConnectionBanner = connectionState[0];
  var setShowConnectionBanner = connectionState[1];

  useEffect(
    function () {
      function onNetworkError() {
        setShowConnectionBanner(true);
      }
      function onNetworkRecovered() {
        setShowConnectionBanner(false);
      }
      function onAuthExpired() {
        // Only clear auth if we're actually logged in — avoids race conditions
        // during page refresh where api.js fires before auth state initializes
        if (localStorage.getItem('authData')) {
          setAuthData(null);
        }
      }
      // When app returns to foreground, do a health check to dismiss the banner
      function onVisibilityChange() {
        if (document.visibilityState === 'visible' && showConnectionBanner) {
          api
            .get('/api/users/me')
            .then(function () {
              setShowConnectionBanner(false);
            })
            .catch(function () {});
        }
      }
      window.addEventListener('network-error', onNetworkError);
      window.addEventListener('network-recovered', onNetworkRecovered);
      window.addEventListener('auth-expired', onAuthExpired);
      document.addEventListener('visibilitychange', onVisibilityChange);
      return function () {
        window.removeEventListener('network-error', onNetworkError);
        window.removeEventListener('network-recovered', onNetworkRecovered);
        window.removeEventListener('auth-expired', onAuthExpired);
        document.removeEventListener('visibilitychange', onVisibilityChange);
      };
    },
    [showConnectionBanner]
  );

  var pollRef = useRef(null);

  const setAuthData = function (data) {
    if (data) {
      localStorage.setItem('authData', JSON.stringify(data));
      setApiAuthToken(data.token);
    } else {
      localStorage.removeItem('authData');
      setApiAuthToken(null);
    }
    setAuth(data);
  };

  // Restore a session from the HttpOnly hr_session cookie when localStorage is
  // gone. This is the half of the cookie work that actually fixes the symptom:
  // the server can hold a valid session, but without this the client would
  // still boot signed-out and prompt for another login.
  useEffect(
    function () {
      if (!restoringSession) return;
      var cancelled = false;
      api
        .get('/api/users/me')
        .then(function (res) {
          if (cancelled || !res.data) return;
          // No token is stored: the cookie is HttpOnly and unreadable here.
          // COOKIE_SESSION keeps isLoggedIn checks working and tells api.js to
          // send no Authorization header so the cookie is used instead.
          setAuthData({ user: res.data, token: COOKIE_SESSION });
          localStorage.setItem('hr_has_logged_in', 'true');
        })
        .catch(function () {
          // 401 means the cookie is gone or expired. Clear the hint so the
          // next load does not wait on a restore that cannot succeed.
          document.cookie = 'hr_session_present=; Max-Age=0; path=/';
        })
        .finally(function () {
          if (!cancelled) setRestoringSession(false);
        });
      return function () {
        cancelled = true;
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(
    function () {
      setApiAuthToken(auth ? auth.token : null);
      if (auth && auth.user) {
        Sentry.setUser({ id: auth.user.id, username: auth.user.username });
        identifyUser(auth.user.id, { username: auth.user.username });
      } else {
        Sentry.setUser(null);
        resetUser();
      }
    },
    [auth]
  );

  var isAuthenticated = auth != null;

  var _showSignupPrompt = useState(false);
  var showSignupPrompt = _showSignupPrompt[0];
  var setShowSignupPrompt = _showSignupPrompt[1];

  // Poll for unread notification count
  useEffect(
    function () {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }

      if (!isAuthenticated) {
        setUnreadCount(0);
        return;
      }

      function fetchCount() {
        api
          .get('/api/notifications/unread-count')
          .then(function (result) {
            setUnreadCount(result.data.count || 0);
          })
          .catch(function () {});
      }

      fetchCount();
      pollRef.current = setInterval(fetchCount, 30000);

      function onNotificationsRead() {
        // Clear immediately for instant feedback, then confirm with server
        setUnreadCount(0);
      }
      window.addEventListener('notifications-read', onNotificationsRead);

      return function () {
        if (pollRef.current) {
          clearInterval(pollRef.current);
        }
        window.removeEventListener('notifications-read', onNotificationsRead);
      };
    },
    [isAuthenticated]
  );

  // Hold the first paint only while a session restore is genuinely in flight,
  // i.e. localStorage was cleared but the session cookie hint is still there.
  // Rendering here would show the signed-out landing page to someone who is
  // about to be signed back in.
  if (restoringSession) {
    return <LoadingText>Loading...</LoadingText>;
  }

  return (
    <Sentry.ErrorBoundary
      fallback={
        <p style={{ textAlign: 'center', marginTop: '2rem' }}>
          Something went wrong. Please refresh the page.
        </p>
      }
    >
      <AuthContext.Provider
        value={{ authTokens: auth, setAuthTokens: setAuthData, isAuthenticated: isAuthenticated }}
      >
        <Router>
          <PageViewTracker />
          <InternalLinkInterceptor />
          <PlaybackProvider>
            <PlayCountsProvider>
              {showConnectionBanner && (
                <ServerDownOverlay>
                  <VinylDisc theme={VINYL_THEMES.test_pressing} size={80} spinning />
                  <ServerDownTitle>
                    <HeavyText>Heavy</HeavyText>
                    <LightItalicText>Rotation</LightItalicText>
                  </ServerDownTitle>
                  <ServerDownMessage>
                    We're having trouble reaching the server. Hang tight, we'll be back shortly.
                  </ServerDownMessage>
                  <ServerDownRefresh
                    onClick={function () {
                      api
                        .get('/api/users/me')
                        .then(function () {
                          setShowConnectionBanner(false);
                        })
                        .catch(function () {
                          window.location.reload();
                        });
                    }}
                  >
                    Try Again
                  </ServerDownRefresh>
                  <button
                    onClick={function () {
                      setShowConnectionBanner(false);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#999',
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      marginTop: '0.5rem',
                      fontFamily: 'inherit',
                    }}
                  >
                    Dismiss
                  </button>
                </ServerDownOverlay>
              )}
              <NavBar>
                <NavLeft>
                  {isAuthenticated ? (
                    <CreateMenu />
                  ) : (
                    <PlusButtonEl
                      onClick={function () {
                        setShowSignupPrompt(true);
                      }}
                    >
                      +
                    </PlusButtonEl>
                  )}
                  <SearchLink to="/music/search">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    >
                      <circle cx="6.5" cy="6.5" r="5.5" />
                      <line x1="10.5" y1="10.5" x2="15" y2="15" />
                    </svg>
                  </SearchLink>
                </NavLeft>
                <NavCenter>
                  <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <HeavyText>Heavy</HeavyText>
                    <LightItalicText>Rotation</LightItalicText>
                  </Link>
                </NavCenter>
                <NavRight>
                  {isAuthenticated && auth?.user ? (
                    <div style={{ position: 'relative' }}>
                      <HamburgerButton
                        onClick={function () {
                          setMenuOpen(!menuOpen);
                        }}
                      >
                        <HamburgerIcon />
                        {unreadCount > 0 && <UnreadDotNav />}
                      </HamburgerButton>
                      {menuOpen && (
                        <>
                          <MenuOverlay
                            onClick={function () {
                              setMenuOpen(false);
                            }}
                          />
                          <MenuDropdown>
                            <MenuItem
                              to="/notifications"
                              onClick={function () {
                                setMenuOpen(false);
                              }}
                            >
                              Notifications
                              {unreadCount > 0 && (
                                <MenuBadge>{unreadCount > 99 ? '99+' : unreadCount}</MenuBadge>
                              )}
                            </MenuItem>
                            <MenuItem
                              to={'/profile/' + auth.user.username}
                              onClick={function () {
                                setMenuOpen(false);
                              }}
                            >
                              Profile
                            </MenuItem>
                            <MenuItem
                              to="/bookmarks"
                              onClick={function () {
                                setMenuOpen(false);
                              }}
                            >
                              Bookmarks
                            </MenuItem>
                            <div style={{ borderTop: '1px solid #999', margin: '2px 0' }} />
                            <MenuItem
                              to="/music/search"
                              onClick={function () {
                                setMenuOpen(false);
                              }}
                            >
                              Music Search
                            </MenuItem>
                            <MenuItem
                              to="/prompts"
                              onClick={function () {
                                setMenuOpen(false);
                              }}
                            >
                              Prompts
                            </MenuItem>
                            <div style={{ borderTop: '1px solid #999', margin: '2px 0' }} />
                            <MenuItem
                              to="/search"
                              onClick={function () {
                                setMenuOpen(false);
                              }}
                            >
                              Find Friends
                            </MenuItem>
                            <MenuItem
                              to="/invites"
                              onClick={function () {
                                setMenuOpen(false);
                              }}
                            >
                              Invite
                            </MenuItem>
                            <div style={{ borderTop: '1px solid #999', margin: '2px 0' }} />
                            <MenuItem
                              to="/help"
                              onClick={function () {
                                setMenuOpen(false);
                              }}
                            >
                              Help
                            </MenuItem>
                            <MenuItem
                              to="/legal"
                              onClick={function () {
                                setMenuOpen(false);
                              }}
                            >
                              Legal
                            </MenuItem>
                            {auth?.user?.isAdmin && (
                              <MenuItem
                                to="/admin"
                                onClick={function () {
                                  setMenuOpen(false);
                                }}
                              >
                                Admin
                              </MenuItem>
                            )}
                            <MenuButton
                              onClick={function () {
                                setMenuOpen(false);
                                api
                                  .post('/api/users/logout')
                                  .then(function () {
                                    setAuthData(null);
                                  })
                                  .catch(function () {
                                    setAuthData(null);
                                  });
                              }}
                            >
                              Log Out
                            </MenuButton>
                          </MenuDropdown>
                        </>
                      )}
                    </div>
                  ) : (
                    <NavLoginLink to="/login">Log in</NavLoginLink>
                  )}
                </NavRight>
              </NavBar>
              <ContentWrapper>
                <React.Suspense fallback={null}>
                  <Routes>
                    <Route path="/" element={isAuthenticated ? <UnifiedFeed /> : <Landing />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/google-callback" element={<GoogleCallback />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/choose-username" element={<ChooseUsername />} />
                    <Route
                      path="/welcome"
                      element={
                        <PrivateRoute>
                          <Welcome />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/profile/edit"
                      element={
                        <PrivateRoute>
                          <EditProfile />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/streaming"
                      element={
                        <PrivateRoute>
                          <StreamingPreferences />
                        </PrivateRoute>
                      }
                    />
                    <Route path="/profile/:userId" element={<Profile />} />
                    <Route
                      path="/post"
                      element={
                        <PrivateRoute>
                          <CreatePost />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/create-list"
                      element={
                        <PrivateRoute>
                          <CreateList />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/list/edit"
                      element={
                        <PrivateRoute>
                          <EditList />
                        </PrivateRoute>
                      }
                    />
                    <Route path="/post/:postId" element={<PostDetail />} />
                    <Route path="/tags/:tagName" element={<TagFeed />} />
                    <Route path="/prompts" element={<Prompts />} />
                    <Route path="/prompts/:promptId" element={<PromptFeed />} />
                    {ENABLE_MUSIC_PAGES && <Route path="/music/search" element={<MusicSearch />} />}
                    {ENABLE_MUSIC_PAGES && (
                      <Route path="/artist/:artistName" element={<ArtistPage />} />
                    )}
                    {ENABLE_MUSIC_PAGES && <Route path="/music/album" element={<SongPage />} />}
                    {ENABLE_MUSIC_PAGES && <Route path="/music/song" element={<SongPage />} />}
                    {ENABLE_MUSIC_PAGES && <Route path="/music/:mediaId" element={<SongPage />} />}
                    <Route
                      path="/search"
                      element={
                        <PrivateRoute>
                          <UserSearch />
                        </PrivateRoute>
                      }
                    />
                    <Route path="/discover" element={<Navigate to="/" replace />} />
                    <Route path="/explore" element={<Navigate to="/" replace />} />
                    <Route
                      path="/bookmarks"
                      element={
                        <PrivateRoute>
                          <Bookmarks />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/invites"
                      element={
                        <PrivateRoute>
                          <Invites />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/notifications"
                      element={
                        <PrivateRoute>
                          <Notifications />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/help"
                      element={
                        <PrivateRoute>
                          <Help />
                        </PrivateRoute>
                      }
                    />
                    <Route path="/guide" element={<Guide />} />
                    <Route path="/legal" element={<Legal />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/disclaimer" element={<Disclaimer />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/verify-email" element={<VerifyEmail />} />
                    <Route
                      path="/admin"
                      element={
                        <AdminRoute>
                          <Admin />
                        </AdminRoute>
                      }
                    />
                  </Routes>
                </React.Suspense>
                <SiteFooter>
                  <FooterLinks>
                    <FooterLink to="/terms">Terms</FooterLink>
                    <FooterLink to="/privacy">Privacy</FooterLink>
                    <FooterLink to="/disclaimer">Disclaimer</FooterLink>
                  </FooterLinks>
                  Apple and Apple Music are trademarks of Apple Inc., registered in the U.S. and
                  other countries.
                </SiteFooter>
              </ContentWrapper>
              <ScrollToTopButton />
              <MiniPlayer />
              {showSignupPrompt && (
                <SignupPromptModal
                  source="nav_plus"
                  onClose={function () {
                    setShowSignupPrompt(false);
                  }}
                />
              )}
            </PlayCountsProvider>
          </PlaybackProvider>
        </Router>
      </AuthContext.Provider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
